import type { AccountType } from '../types/account.js';
import type {
  AccountAnalyticsFilterOptions,
  AccountAnalyticsFilters,
  AccountAnalyticsRow,
  AccountAnalyticsStats,
  AmDirectoryEntry,
  AnalyticsBreakdown,
  AnalyticsFilterOption,
} from '../types/accountAnalytics.js';

// The heart of the Account Analytics dashboard — pure, heavily tested, and
// reused VERBATIM in Phase 2 (aggregate(applyDraft(rows, draft))). The client
// fetches the full roster once and calls these to filter / aggregate / derive
// options entirely client-side.

// The default "count of accounts" set — the operational leaf types. Future
// operational categories (e.g. ecommerce, local business, other) append here so
// the scope toggle keeps working without a rewrite (build-plan Open item 1).
export const OPERATIONAL_ACCOUNT_TYPES: readonly AccountType[] = ['location', 'dealership'];

// Filter sentinel: match rows with NO account manager. Included in the
// accountManagerId filter array to select unassigned accounts.
export const UNASSIGNED_AM = '__unassigned__';

function isOperational(type: AccountType): boolean {
  return OPERATIONAL_ACCOUNT_TYPES.includes(type);
}

// ---------- filtering ----------

// Apply the active filters. Every dimension is an OR-within / AND-across match;
// an empty array is "no constraint". Scope narrows to operational types (or
// leaves every type). Returns the surviving rows (order preserved).
export function filterRoster(
  rows: AccountAnalyticsRow[],
  filters: AccountAnalyticsFilters,
): AccountAnalyticsRow[] {
  return rows.filter((row) => {
    if (filters.scope === 'operational' && !isOperational(row.accountType)) return false;

    if (filters.brand.length) {
      if (row.brandAccountId == null || !filters.brand.includes(row.brandAccountId)) return false;
    }

    if (filters.accountManagerId.length) {
      const matchesAssigned =
        row.accountManagerId != null && filters.accountManagerId.includes(row.accountManagerId);
      const matchesUnassigned =
        row.accountManagerId == null && filters.accountManagerId.includes(UNASSIGNED_AM);
      if (!matchesAssigned && !matchesUnassigned) return false;
    }

    if (filters.group.length) {
      if (row.group == null || !filters.group.includes(row.group)) return false;
    }

    if (filters.industry.length && !filters.industry.includes(row.industry)) return false;
    if (filters.accountType.length && !filters.accountType.includes(row.accountType)) return false;
    if (filters.status.length && !filters.status.includes(row.status)) return false;

    return true;
  });
}

// ---------- aggregation ----------

function countBy<T>(
  rows: AccountAnalyticsRow[],
  keyFn: (row: AccountAnalyticsRow) => T | null,
  labelFn: (key: T) => string,
): AnalyticsBreakdown[] {
  const counts = new Map<string, { key: string; label: string; count: number }>();
  for (const row of rows) {
    const raw = keyFn(row);
    if (raw == null) continue;
    const key = String(raw);
    const existing = counts.get(key);
    if (existing) existing.count += 1;
    else counts.set(key, { key, label: labelFn(raw), count: 1 });
  }
  // Sort desc by count, tiebreak by label asc (deterministic).
  return Array.from(counts.values()).sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  );
}

function distinct<T>(rows: AccountAnalyticsRow[], keyFn: (row: AccountAnalyticsRow) => T | null): number {
  const set = new Set<T>();
  for (const row of rows) {
    const v = keyFn(row);
    if (v != null) set.add(v);
  }
  return set.size;
}

export function humanizeIndustry(industry: string): string {
  return industry.replace(/_/g, ' ');
}

export function humanizeAccountType(type: string): string {
  return type.replace(/_/g, ' ');
}

function capitalize(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// The aggregate DTO the charts read. Operates on whatever rows it's given (the
// caller passes the already-filtered set); totalOperational counts the
// operational subset within that set, totalAll counts all of it.
export function aggregate(rows: AccountAnalyticsRow[]): AccountAnalyticsStats {
  return {
    totalOperational: rows.filter((r) => isOperational(r.accountType)).length,
    totalAll: rows.length,
    brands: distinct(rows, (r) => r.brandAccountId),
    groups: distinct(rows, (r) => r.group),
    accountManagers: distinct(rows, (r) => r.accountManagerId),
    industries: distinct(rows, (r) => r.industry),
    byAccountManager: countBy(
      rows,
      (r) => r.accountManagerId,
      (uid) => rows.find((r) => r.accountManagerId === uid)?.accountManagerName ?? 'Unknown',
    ),
    byBrand: countBy(
      rows,
      (r) => r.brandAccountId,
      (id) => rows.find((r) => r.brandAccountId === id)?.brandName ?? 'Unknown',
    ),
    byGroup: countBy(rows, (r) => r.group, (g) => g),
    byIndustry: countBy(rows, (r) => r.industry, humanizeIndustry),
    byType: countBy(rows, (r) => r.accountType, humanizeAccountType),
    byStatus: countBy(rows, (r) => r.status, capitalize),
  };
}

// ---------- filter options ----------

function optionsFrom(
  rows: AccountAnalyticsRow[],
  keyFn: (row: AccountAnalyticsRow) => string | null,
  labelFn: (row: AccountAnalyticsRow, key: string) => string,
): AnalyticsFilterOption[] {
  const map = new Map<string, string>();
  for (const row of rows) {
    const key = keyFn(row);
    if (key == null) continue;
    if (!map.has(key)) map.set(key, labelFn(row, key));
  }
  return Array.from(map.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// Derive the distinct filter-bar options present in the roster. AMs include an
// "Unassigned" sentinel option when any row has no AM. Options are derived from
// the FULL roster (not the filtered set) so they stay stable as the user filters.
export function deriveFilterOptions(rows: AccountAnalyticsRow[]): AccountAnalyticsFilterOptions {
  const accountManagers = optionsFrom(
    rows,
    (r) => r.accountManagerId,
    (r) => r.accountManagerName ?? r.accountManagerId ?? 'Unknown',
  );
  if (rows.some((r) => r.accountManagerId == null)) {
    accountManagers.push({ value: UNASSIGNED_AM, label: 'Unassigned' });
  }

  return {
    brands: optionsFrom(rows, (r) => r.brandAccountId, (r) => r.brandName ?? r.brandAccountId ?? 'Unknown'),
    accountManagers,
    groups: optionsFrom(rows, (r) => r.group, (_r, key) => key),
    industries: optionsFrom(rows, (r) => r.industry, (r) => humanizeIndustry(r.industry)),
    accountTypes: optionsFrom(rows, (r) => r.accountType, (r) => humanizeAccountType(r.accountType)),
    statuses: optionsFrom(rows, (r) => r.status, (r) => capitalize(r.status)),
  };
}

// ---------- AM × brand (grouped bar chart) ----------

// The bucket label for accounts with no brand ancestor, so the AM×brand totals
// reconcile with the headline count (every account lands in exactly one brand
// series). Sorted LAST in the brand series so it reads as the residual.
export const NO_BRAND_KEY = '(no brand)';

// A recharts-ready dataset for the grouped "accounts per AM per brand" chart:
// one `data` row per AM (carrying a count property per brand key), plus the
// ordered `brands` list for the <Bar> series + legend.
export type AmBrandChartRow = { amKey: string; amLabel: string } & Record<string, string | number>;

export type AmBrandChartData = {
  data: AmBrandChartRow[];
  brands: string[];
};

// Aggregate the already-filtered / already-scoped rows into the AM×brand matrix.
// Does NOT re-filter — the caller passes the same memoised filtered set the other
// charts read, so the scope toggle + active filters flow through automatically.
// AMs are ordered by total desc (tiebreak label asc), matching the per-AM bar;
// brands by total desc (tiebreak name asc), with the "(no brand)" bucket last.
// Every AM row carries a count for EVERY brand key (0-filled) so recharts renders
// consistent grouped bars.
export function aggregateByAmAndBrand(rows: AccountAnalyticsRow[]): AmBrandChartData {
  const ams = new Map<
    string,
    { amKey: string; amLabel: string; total: number; counts: Map<string, number> }
  >();
  const brandTotals = new Map<string, number>();

  for (const row of rows) {
    const amKey = row.accountManagerId ?? UNASSIGNED_AM;
    const amLabel =
      row.accountManagerId == null ? 'Unassigned' : row.accountManagerName ?? 'Unknown';
    const brandKey = row.brandName ?? NO_BRAND_KEY;

    let am = ams.get(amKey);
    if (!am) {
      am = { amKey, amLabel, total: 0, counts: new Map() };
      ams.set(amKey, am);
    }
    am.total += 1;
    am.counts.set(brandKey, (am.counts.get(brandKey) ?? 0) + 1);
    brandTotals.set(brandKey, (brandTotals.get(brandKey) ?? 0) + 1);
  }

  const brands = Array.from(brandTotals.keys()).sort((a, b) => {
    // "(no brand)" always sorts last.
    if (a === NO_BRAND_KEY) return b === NO_BRAND_KEY ? 0 : 1;
    if (b === NO_BRAND_KEY) return -1;
    return (brandTotals.get(b) ?? 0) - (brandTotals.get(a) ?? 0) || a.localeCompare(b);
  });

  const data: AmBrandChartRow[] = Array.from(ams.values())
    .sort((a, b) => b.total - a.total || a.amLabel.localeCompare(b.amLabel))
    .map((am) => {
      const rowObj: AmBrandChartRow = { amKey: am.amKey, amLabel: am.amLabel };
      for (const brand of brands) rowObj[brand] = am.counts.get(brand) ?? 0;
      return rowObj;
    });

  return { data, brands };
}

// ---------- Phase 2: draft AM re-assignment ----------

// The in-memory draft: staged AM uid per account (null = unassign). An account
// absent from the map keeps its current AM; a staged AM equal to the account's
// current AM is a NO-OP (excluded from moves/deltas/commit). Roster-wide —
// independent of the active filters/scope.
export type AccountDraft = Map<string, string | null>;

// The label shown for an AM in draft previews: null → "Unassigned"; an unknown
// uid → "Unknown" (a real but unresolvable manager).
const UNASSIGNED_LABEL = 'Unassigned';

// A single effective account move (no-ops excluded).
export type DraftMove = {
  accountId: string;
  name: string;
  fromAmId: string | null;
  fromAmLabel: string;
  toAmId: string | null;
  toAmLabel: string;
};

// Per-AM net change under the draft.
export type AmDelta = {
  amKey: string; // uid, or UNASSIGNED_AM for the unassigned bucket
  amLabel: string;
  before: number;
  after: number;
  delta: number;
};

// Build uid -> display label, preferring the AM directory, falling back to any
// name already carried on the rows. A real uid with no resolvable name → "Unknown".
function buildAmLabelResolver(
  rows: AccountAnalyticsRow[],
  amDirectory: AmDirectoryEntry[],
): (id: string | null) => string {
  const map = new Map<string, string>();
  for (const r of rows) {
    if (r.accountManagerId != null && r.accountManagerName != null && !map.has(r.accountManagerId)) {
      map.set(r.accountManagerId, r.accountManagerName);
    }
  }
  // Directory wins (freshest source of the display name).
  for (const e of amDirectory) {
    if (e.displayName != null) map.set(e.uid, e.displayName);
  }
  return (id) => (id == null ? UNASSIGNED_LABEL : map.get(id) ?? 'Unknown');
}

// Apply the draft to the roster: override accountManagerId + accountManagerName
// per the staged AM (resolved from the directory; null → unassigned). Pure —
// returns a NEW array; no-ops and absent accounts pass through unchanged. This is
// the single seam the whole dashboard re-aggregates through in draft mode.
export function applyDraft(
  rows: AccountAnalyticsRow[],
  draft: AccountDraft,
  amDirectory: AmDirectoryEntry[],
): AccountAnalyticsRow[] {
  if (draft.size === 0) return rows;
  const dirName = new Map<string, string | null>(amDirectory.map((e) => [e.uid, e.displayName]));
  return rows.map((row) => {
    if (!draft.has(row.id)) return row;
    const staged = draft.get(row.id) ?? null;
    if (staged === row.accountManagerId) return row; // no-op
    return {
      ...row,
      accountManagerId: staged,
      accountManagerName: staged == null ? null : dirName.get(staged) ?? null,
    };
  });
}

// Collect the id set under a group: the group node + every descendant, walking
// parentAccountId DOWNWARD (children map + DFS). Handles multi-level nesting
// (group → sub-group → location) and is cycle-guarded via the visited set.
export function collectGroupDescendants(
  rows: Array<Pick<AccountAnalyticsRow, 'id' | 'parentAccountId'>>,
  groupAccountId: string,
): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const r of rows) {
    if (r.parentAccountId != null) {
      const arr = childrenByParent.get(r.parentAccountId);
      if (arr) arr.push(r.id);
      else childrenByParent.set(r.parentAccountId, [r.id]);
    }
  }
  const out = new Set<string>();
  const stack = [groupAccountId];
  while (stack.length) {
    const id = stack.pop() as string;
    if (out.has(id)) continue;
    out.add(id);
    for (const child of childrenByParent.get(id) ?? []) stack.push(child);
  }
  return out;
}

// The effective moves (no-ops excluded), each with resolved from/to labels.
// Roster-wide (does not honour filters) — the commit acts on the whole draft.
export function draftMoves(
  rows: AccountAnalyticsRow[],
  draft: AccountDraft,
  amDirectory: AmDirectoryEntry[],
): DraftMove[] {
  const label = buildAmLabelResolver(rows, amDirectory);
  const moves: DraftMove[] = [];
  for (const row of rows) {
    if (!draft.has(row.id)) continue;
    const staged = draft.get(row.id) ?? null;
    if (staged === row.accountManagerId) continue; // no-op
    moves.push({
      accountId: row.id,
      name: row.name,
      fromAmId: row.accountManagerId,
      fromAmLabel: label(row.accountManagerId),
      toAmId: staged,
      toAmLabel: label(staged),
    });
  }
  return moves;
}

// Per-AM net change under the draft — before/after counts + delta for every AM
// the draft touches (delta !== 0). Sorted gains-first (delta desc, tiebreak label
// asc). Powers the "Alice +5, Bob −3, Unassigned −2" preview.
export function draftDeltaByAm(
  rows: AccountAnalyticsRow[],
  draft: AccountDraft,
  amDirectory: AmDirectoryEntry[],
): AmDelta[] {
  const label = buildAmLabelResolver(rows, amDirectory);
  const applied = applyDraft(rows, draft, amDirectory);
  const before = new Map<string, number>();
  const after = new Map<string, number>();
  const keyOf = (id: string | null) => id ?? UNASSIGNED_AM;
  for (const r of rows) before.set(keyOf(r.accountManagerId), (before.get(keyOf(r.accountManagerId)) ?? 0) + 1);
  for (const r of applied) after.set(keyOf(r.accountManagerId), (after.get(keyOf(r.accountManagerId)) ?? 0) + 1);

  const keys = new Set([...before.keys(), ...after.keys()]);
  const out: AmDelta[] = [];
  for (const amKey of keys) {
    const b = before.get(amKey) ?? 0;
    const a = after.get(amKey) ?? 0;
    if (a - b === 0) continue; // untouched
    out.push({
      amKey,
      amLabel: amKey === UNASSIGNED_AM ? UNASSIGNED_LABEL : label(amKey),
      before: b,
      after: a,
      delta: a - b,
    });
  }
  return out.sort((x, y) => y.delta - x.delta || x.amLabel.localeCompare(y.amLabel));
}

// Group effective moves by TARGET AM for the commit — one bucket per distinct
// target (null = Unassign), each carrying the account ids moving to it. The
// commit fires one bulk-assign call per bucket. Pure so the grouping is tested
// independently of the network path.
export function groupMovesByTarget(
  moves: DraftMove[],
): Array<{ accountManagerId: string | null; accountIds: string[] }> {
  const byTarget = new Map<string | null, string[]>();
  for (const m of moves) {
    const arr = byTarget.get(m.toAmId);
    if (arr) arr.push(m.accountId);
    else byTarget.set(m.toAmId, [m.accountId]);
  }
  return Array.from(byTarget.entries()).map(([accountManagerId, accountIds]) => ({
    accountManagerId,
    accountIds,
  }));
}
